// BrokerLoginPanel — Angel One login form (light theme)
import React, { useState } from 'react';
import { useAngelOneAuth } from '../../../hooks/useAngelOneAuth';
import { FaSignInAlt, FaSignOutAlt, FaTimes, FaSync, FaUser } from 'react-icons/fa';

export default function BrokerLoginPanel() {
  const { login, logout, loading, error, isConnected, profile, clientcode } = useAngelOneAuth();
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    clientcode: '',
    password: '',
    totp: '',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    const result = await login(form.clientcode, form.password, form.totp);
    if (result.success) {
      setShowModal(false);
      setForm({ clientcode: '', password: '', totp: '' });
    }
  };

  if (isConnected) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 bg-white/20 rounded-lg px-3 py-1.5">
          <FaUser className="text-[10px] text-white/80" />
          <div className="text-right">
            <p className="text-[11px] font-semibold text-white">{profile?.name || clientcode}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="p-2 rounded-lg text-white/70 hover:text-red-300 hover:bg-white/10 transition-all"
          title="Disconnect from Angel One"
        >
          <FaSignOutAlt className="text-xs" />
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="px-4 py-2 rounded-lg text-xs font-semibold bg-white text-blue-600 hover:bg-blue-50 transition-all flex items-center gap-1.5 shadow-sm"
      >
        <FaSignInAlt className="text-[10px]" /> Connect Angel One
      </button>

      {showModal && (
        <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4" onClick={() => !loading && setShowModal(false)}>
          <div className="w-full max-w-sm bg-white rounded-xl shadow-xl border border-gray-200" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <img src="https://www.angelone.in/favicon.ico" alt="" className="w-4 h-4" />
                  Angel One Login
                </h3>
                <p className="text-[11px] text-gray-500 mt-0.5">Enter your SmartAPI credentials</p>
              </div>
              <button
                onClick={() => !loading && setShowModal(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <FaTimes className="text-xs" />
              </button>
            </div>

            <form onSubmit={handleLogin} className="p-5 space-y-3">
              <div>
                <label className="text-[11px] font-medium text-gray-600 block mb-1">Client Code</label>
                <input
                  value={form.clientcode}
                  onChange={e => setForm({ ...form, clientcode: e.target.value.toUpperCase() })}
                  placeholder="e.g., K12345"
                  autoComplete="username"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-600 block mb-1">PIN / Password</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="Your Angel One PIN"
                  autoComplete="current-password"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-600 block mb-1">TOTP</label>
                <input
                  value={form.totp}
                  onChange={e => setForm({ ...form, totp: e.target.value.replace(/\D/g, '') })}
                  placeholder="6-digit TOTP from authenticator app"
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono text-gray-900 tracking-[0.3em] text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {error && (
                <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg border border-red-100">
                  <p className="font-medium">Connection failed</p>
                  <p className="mt-0.5 text-red-500">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !form.clientcode || !form.password || !form.totp}
                className="w-full bg-blue-600 text-white text-sm font-semibold py-3 rounded-lg hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <FaSync className="text-xs animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <FaSignInAlt /> Connect
                  </>
                )}
              </button>

              <p className="text-[10px] text-gray-400 text-center">
                API key hardcoded server-side. Tokens stored securely in Firebase.
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
